import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Phone, Clock, User, MessageSquare, Trash2 } from 'lucide-react';
import { formatPhoneDisplay } from '@/utils/phoneUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactRequests = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/contact-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(response.data);
    } catch (error) {
      toast.error('Failed to fetch contact requests');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-white border-b border-stone-200 py-3 md:py-4 px-4 md:px-6 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/admin/dashboard')} variant="ghost" size="sm" className="text-stone-600 hover:text-[#1A4D2E]">
              <ArrowLeft size={18} className="mr-2" />Back
            </Button>
            <h1 className="text-xl md:text-3xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>
              Contact Us Requests
            </h1>
          </div>
          <span className="text-sm text-stone-500">{submissions.length} requests</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A4D2E] mx-auto"></div>
            <p className="mt-4 text-stone-600">Loading requests...</p>
          </div>
        ) : submissions.length === 0 ? (
          <Card className="p-16 text-center bg-white">
            <MessageSquare size={48} className="mx-auto text-stone-300 mb-4" />
            <h3 className="text-lg font-semibold text-stone-700 mb-2">No Contact Requests Yet</h3>
            <p className="text-stone-500">When visitors submit the Contact Us form, their requests will appear here.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {submissions.map((sub, idx) => (
              <Card key={sub.id || idx} className="bg-white p-5 md:p-6 hover:shadow-md transition-shadow" data-testid={`contact-request-${idx}`}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Header row */}
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-[#1A4D2E]/10 flex items-center justify-center flex-shrink-0">
                        <User size={20} className="text-[#1A4D2E]" />
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-stone-900">{sub.name}</h3>
                        {sub.subject && <p className="text-sm font-medium text-[#D4A373]">{sub.subject}</p>}
                      </div>
                    </div>

                    {/* Contact info */}
                    <div className="flex flex-wrap gap-4 mb-3 text-sm text-stone-600">
                      <span className="flex items-center gap-1.5">
                        <Mail size={14} className="text-stone-400" />
                        <a href={`mailto:${sub.email}`} className="hover:text-[#1A4D2E] underline">{sub.email}</a>
                      </span>
                      {sub.phone && (
                        <span className="flex items-center gap-1.5">
                          <Phone size={14} className="text-stone-400" />
                          {formatPhoneDisplay(sub.phone)}
                        </span>
                      )}
                      <span className="flex items-center gap-1.5">
                        <Clock size={14} className="text-stone-400" />
                        {formatDate(sub.submitted_at)}
                      </span>
                    </div>

                    {/* Message */}
                    <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
                      <p className="text-sm text-stone-700 whitespace-pre-line">{sub.message}</p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactRequests;
